import { compact } from 'lodash'
import moment from 'moment/moment'

import { TagProps } from '../components/app/design/Tag'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { currencyFloat } from '../utils/numbers'
import { Option } from './Option'
import { Payment } from './Payment'
import { FKType } from './SQLBuilder'
import { Transactable } from './Transactable'
import { Transaction } from './Transaction'

type ChargeConstructorArgs = ConstructorArgs<() => Charge>

export class Charge extends Transaction implements Transactable {
    readonly recordType = 'charge'

    readonly iTransactable = true

    memo?: string
    markPaidDate?: moment.Moment

    constructor(data?: Partial<ChargeConstructorArgs>) {
        super(data)
        if (!data) {
            return
        }

        this.markPaidDate = this.propertyFromTimestampArg(data.mark_paid_date)
        this.memo = data.memo ?? undefined
    }

    clone(overrides?: Partial<ChargeConstructorArgs>): Charge {
        return new Charge({ ...this.serializeForClone(), ...overrides })
    }

    get chargeAmount(): number {
        return (this.amount ?? 0) + this.taxAmount + this.gratuityAmount
    }

    get gratuityAmount(): number {
        if (this.gratuity && this.gratuity > 0) {
            let gratuity = this.gratuity
            if (this.gratuityType === 'percent' && this.amount !== undefined)
                gratuity = currencyFloat((this.gratuity * this.amount) / 100)
            return gratuity
        }
        return 0
    }

    get discountAmount(): number {
        if (this.discount && this.discount > 0) {
            let discount = this.discount
            const total = this.discountableTotal!
            if (this.discountType === 'percent')
                discount = currencyFloat((this.discount * (total ?? 0)) / 100)
            else if (discount > total) discount = total
            return discount
        }
        return 0
    }

    get paidAmount(): number | undefined {
        return this.markPaidDate !== undefined ? this.chargeAmount : 0
    }

    get itemCount(): number {
        let count = 0
        for (const line of this.lines) {
            if (line.type === 'item') count++
        }
        return count
    }

    get statusString(): string {
        const payment = this.payment
        if (payment) return payment.statusString
        if (this.cancelledDate) return `Canceled on ${this.cancelledDate.format('MMM D, YYYY')}`
        if (this.markPaidDate && this.paymeth)
            return `Mark paid ${this.markPaidDate.format('MMM D, YYYY')} - ${this.paymeth}`
        if (this.markPaidDate) return `Mark paid ${this.markPaidDate.format('MMM D, YYYY')}`
        return `Paid ${this.paidDate?.format('MMM D, YYYY')}`
    }

    get statuses(): {
        title: string
        date: moment.Moment | undefined
        intent: TagProps['intent']
    }[] {
        return compact([
            {
                title: this.markPaidDate ? 'Marked paid' : 'Paid',
                date: this.markPaidDate ?? this.createdDate,
                intent: 'info',
            },
            {
                title: 'Sent',
                date: this.sentDate,
                intent: 'success',
            },
            !!this.cancelledDate
                ? {
                      title: 'Refunded',
                      date: this.cancelledDate,
                      intent: 'danger',
                  }
                : null,
        ])
    }

    get statusTag(): { label: string; intent: TagProps['intent'] } | null {
        const lastActiveStatus = this.statuses.findLast((status) => status.date !== undefined)
        if (!lastActiveStatus) {
            return null
        }

        return { label: lastActiveStatus.title, intent: lastActiveStatus.intent }
    }

    get taxAmount(): number {
        if (this.tax && this.amount !== undefined)
            return (this.tax * (this.taxableAmount ?? 0)) / 100
        return 0
    }

    get taxableAmount(): number {
        const taxcode = this.taxcode
        let taxableAmount = taxcode !== undefined ? (this.amount ?? 0) : 0
        if (this.lines.length > 0 && taxcode !== undefined) {
            taxableAmount = 0
            let total = 0
            for (const line of this.lines) {
                total += line.amount ?? 0
                if (line.taxable && line.amount !== undefined) taxableAmount += line.amount
            }
            if (this.amount !== undefined && total > 0)
                taxableAmount = currencyFloat((taxableAmount * this.amount) / total)
        }
        return taxableAmount
    }

    static refundReasonStrings(): Option[] {
        return [
            new Option(undefined, { id: 'cancel', label: 'Services not rendered' }),
            new Option(undefined, { id: 'overcharge', label: 'Overcharged' }),
            new Option(undefined, { id: 'courtesy', label: 'Courtesy refund' }),
            new Option(undefined, { id: 'paid', label: 'Client already paid' }),
        ]
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.markPaidDate) this.markPaidDate = moment(data.markPaidDate)
    }

    static fk: FKType = {
        ...Transaction.fk,
    }

    static dataType(): DataType {
        return 'charges'
    }

    get payment(): Payment | undefined {
        return this.getForeignRecord('payment', Payment)
    }
    set payment(payment: Payment | undefined) {
        this.setForeignRecord('payment', payment)
    }
}
